// PaywallScreen.tsx
// 4-step paywall flow controller
// Manages state and navigation between: Value → Plan → Reminder → Checkout

import React, { useState, useEffect, useRef } from 'react';
import { View, ActivityIndicator, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { PageContainer, Text, InstallAppPrompt } from '../../components/ui';
import { Theme } from '../../utils/theme';
import { useSubscription } from '../../hooks/useSubscription';
import { SubscriptionService, PurchasesOfferings } from '../../services/subscriptionService';
import { TrialReminderService } from '../../services/trialReminderService';
import { errorReporting } from '../../services/errorReportingService';
import { auth } from '../../config/firebase';
import { loggingService } from '../../services/loggingService';
import { 
  paywallViewed, 
  paywallStepChanged, 
  paywallDismissed,
  packageSelected,
  purchaseStarted,
  purchaseCompleted,
  purchaseFailed,
  purchaseCancelled,
  restorePurchasesStarted,
  restorePurchasesFailed,
} from '../../utils/logging/actions';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import type { PaywallStep, PaywallSelection } from './types';
import { ValueAndTrustStep } from './steps/ValueAndTrustStep';
import { PlanSelectionStep } from './steps/PlanSelectionStep';
import { TrialReminderStep } from './steps/TrialReminderStep';

export const PaywallScreen: React.FC = () => {
  useNavigationLogging('PaywallScreen');
  
  const navigation = useNavigation();
  const { purchasePackage, restorePurchases } = useSubscription();
  const isWeb = Platform.OS === 'web';
  
  const [currentStep, setCurrentStep] = useState<PaywallStep>('value');
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(isWeb);
  const [selection, setSelection] = useState<PaywallSelection>({
    tier: 'ai', // Default to AI Platinum
    period: 'annual', // Default to annual
    package: null,
    reminderDays: null,
  });
  
  const paywallStartTime = useRef(Date.now());

  useEffect(() => {
    if (isWeb) {
      return;
    }

    loadOfferings();
    
    // Log paywall viewed
    loggingService.log(paywallViewed('unknown'));
  }, [isWeb]);

  const loadOfferings = async () => {
    try {
      const result = await SubscriptionService.getOfferings();
      if (result.success && result.data) {
        setOfferings(result.data);
      } else {
        Toast.show({
          type: 'error',
          text1: 'Failed to load pricing',
          text2: result.error || 'Please try again',
        });
      }
    } catch (error) {
      console.error('[PaywallScreen] Load offerings error:', error);
      errorReporting.reportError(error, {
        userId: auth.currentUser?.uid,
        screen: 'PaywallScreen',
        action: 'loadOfferings',
      });
      Toast.show({
        type: 'error',
        text1: 'Failed to load pricing',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSelection = (updates: Partial<PaywallSelection>) => {
    setSelection((prev) => {
      const newSelection = { ...prev, ...updates };
      
      // Log package selection if package changed
      if (updates.package && updates.package !== prev.package) {
        const pkg = updates.package;
        loggingService.log(packageSelected(
          pkg.identifier,
          newSelection.tier || 'unknown',
          newSelection.period || 'unknown',
          pkg.product.priceString
        ));
      }
      
      return newSelection;
    });
  };

  const handleNext = () => {
    const fromStep = currentStep;
    let toStep: PaywallStep = currentStep;
    
    if (currentStep === 'value') {
      // After value, check if we need to show trial reminder
      const hasTrial = SubscriptionService.offeringHasTrial(offerings);
      if (hasTrial) {
        toStep = 'reminder';
      } else {
        toStep = 'plan';
      }
      setCurrentStep(toStep);
    } else if (currentStep === 'reminder') {
      toStep = 'plan';
      setCurrentStep(toStep);
    }
    
    // Log step change
    if (fromStep !== toStep) {
      loggingService.log(paywallStepChanged(fromStep, toStep));
    }
    // No more 'checkout' step - purchase happens directly from plan selection
  };

  const handleBack = () => {
    const fromStep = currentStep;
    let toStep: PaywallStep = currentStep;
    
    if (currentStep === 'reminder') {
      toStep = 'value';
      setCurrentStep(toStep);
    } else if (currentStep === 'plan') {
      // Check if we showed reminder step
      const hasTrial = SubscriptionService.offeringHasTrial(offerings);
      
      if (hasTrial) {
        toStep = 'reminder';
      } else {
        toStep = 'value';
      }
      setCurrentStep(toStep);
    }
    
    // Log step change
    if (fromStep !== toStep) {
      loggingService.log(paywallStepChanged(fromStep, toStep));
    }
  };

  const handleClose = () => {
    // Log paywall dismissed
    const timeSpent = Date.now() - paywallStartTime.current;
    loggingService.log(paywallDismissed(currentStep, timeSpent));
    
    navigation.goBack();
  };

  const handlePurchase = async (packageToPurchase?: any) => {
    const pkg = packageToPurchase || selection.package;
    
    try {
      if (!pkg) {
        Toast.show({
          type: 'error',
          text1: 'No plan selected',
          text2: 'Please select a plan',
        });
        return;
      }

      setIsPurchasing(true);
      
      // Log purchase started
      loggingService.log(purchaseStarted(
        pkg.identifier,
        selection.tier || 'unknown',
        selection.period || 'unknown',
        pkg.product.priceString
      ));

      const result = await purchasePackage(pkg);
      
      if (result === 'success') {
        // Log purchase completed
        loggingService.log(purchaseCompleted(
          pkg.identifier,
          selection.tier || 'unknown',
          selection.period || 'unknown',
          pkg.product.priceString
        ));
        
        // Schedule trial reminder if user selected one
        if (selection.reminderDays && pkg) {
          const trialDays = SubscriptionService.getTrialDays(pkg);
          if (trialDays) {
            const trialEndDate = new Date();
            trialEndDate.setDate(trialEndDate.getDate() + trialDays);
            
            await TrialReminderService.scheduleTrialReminder(
              trialEndDate,
              selection.reminderDays,
              pkg.product.identifier
            );
          }
        }

        Toast.show({
          type: 'success',
          text1: 'Welcome to Mentorist!',
          text2: 'Your subscription is now active',
        });
        
        navigation.goBack();
      } else if (result === 'cancelled') {
        // Log purchase cancelled
        loggingService.log(purchaseCancelled(
          pkg.identifier,
          selection.tier,
          selection.period
        ));
        
        Toast.show({
          type: 'info',
          text1: 'Purchase was cancelled',
        });
      } else {
        // Log purchase failed
        loggingService.log(purchaseFailed(
          pkg.identifier,
          'Purchase failed',
          selection.tier,
          selection.period
        ));
        
        Toast.show({
          type: 'error',
          text1: 'Purchase Failed',
          text2: 'Please try again',
        });
      }
    } catch (error) {
      console.error('[PaywallScreen] Purchase error:', error);
      
      // Log purchase failed
      if (pkg) {
        loggingService.log(purchaseFailed(
          pkg.identifier,
          error instanceof Error ? error.message : 'Unknown error',
          selection.tier,
          selection.period
        ));
      }
      
      errorReporting.reportError(error, {
        userId: auth.currentUser?.uid,
        screen: 'PaywallScreen',
        action: 'purchasePackage',
        metadata: {
          selectedPlan: pkg?.identifier,
          platform: Platform.OS,
        },
      });
      Toast.show({
        type: 'error',
        text1: 'Purchase Error',
        text2: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRestore = async () => {
    try {
      setIsRestoring(true);
      
      // Log restore started
      loggingService.log(restorePurchasesStarted('manual'));
      
      const success = await restorePurchases('manual');
      
      // Note: Success is logged in SubscriptionContext
      
      if (success) {
        Toast.show({
          type: 'success',
          text1: 'Purchases Restored',
          text2: 'Your subscription has been restored',
        });
        navigation.goBack();
      } else {
        // Log restore failed
        loggingService.log(restorePurchasesFailed('manual', 'No purchases found'));
        
        Toast.show({
          type: 'error',
          text1: 'No Purchases Found',
          text2: 'No previous purchases to restore',
        });
      }
    } catch (error) {
      // Log restore failed
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      loggingService.log(restorePurchasesFailed('manual', errorMessage));
      
      Toast.show({
        type: 'error',
        text1: 'Restore Failed',
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const stepProps = {
    selection,
    onUpdateSelection: updateSelection,
    onNext: handleNext,
    onBack: handleBack,
    onClose: handleClose,
    offerings,
  };

  if (isWeb) {
    return (
      <PageContainer>
        <InstallAppPrompt
          visible={showInstallPrompt}
          onDismiss={() => {
            setShowInstallPrompt(false);
            navigation.goBack();
          }}
        />
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={Theme.colors.primary} />
          <Text variant="body1" style={{ marginTop: Theme.spacing.md, color: Theme.colors.textSecondary }}>
            Loading pricing...
          </Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {currentStep === 'value' && <ValueAndTrustStep {...stepProps} />}
      {currentStep === 'reminder' && <TrialReminderStep {...stepProps} />}
      {currentStep === 'plan' && (
        <PlanSelectionStep
          {...stepProps}
          isPurchasing={isPurchasing}
          onPurchase={handlePurchase}
          onRestore={handleRestore}
          isRestoring={isRestoring}
        />
      )}
    </PageContainer>
  );
};

